import React, { useState, useEffect } from 'react';
import { StyleSheet, View, Switch, Modal, TouchableOpacity, ScrollView, Text } from 'react-native';
import { useTheme } from '@emotion/react';
import { ODSIcon } from '../../../atoms/ODSIcon';
import { ODSText } from '../../../atoms/ODSText';
import ODSButton from '../../../atoms/ODSButton';

// Firebase — web bundle only
import { getToken } from 'firebase/messaging';
import { messaging } from '../../../../../src/helpers/firebase';
import { _put, SERVER_URL } from '../../../../../src/helpers/connectionHelpers';
import { VAPID_KEY } from '../../../../utils/firebaseConfig';

type PermissionStatus = 'granted' | 'denied' | 'default' | 'unsupported';

const getPermissionStatus = (): PermissionStatus => {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission as PermissionStatus;
};

let lastSyncedSettingsToken: string | null = null;
const updateFcmToken = (token: string) => {
  if (lastSyncedSettingsToken === token) return;
  lastSyncedSettingsToken = token;
  const payload = { fcm_token: token };
  _put(
    SERVER_URL + '/update-fcm-token',
    {},
    JSON.stringify(payload),
    (res: any) => console.log('FCM token updated', res),
    (err: any) => {
      lastSyncedSettingsToken = null;
      console.log('FCM token update failed', err);
    },
  );
};

// ─── Notification Permission Guide Overlay ────────────────────────────────────
// Uses plain <Text> (not ODSText) inside the modal to avoid prop-spreading issues
const NotificationGuideOverlay = ({
  visible,
  onClose,
}: {
  visible: boolean;
  onClose: () => void;
}) => {
  if (!visible) return null;

  const steps = [
    {
      number: '1',
      title: '⊸  Click the site-info icon',
      description:
        "Look at the left side of your browser's address bar. Click the tune / site-info icon (it looks like horizontal sliders ⊸ or an ℹ️ info symbol).",
    },
    {
      number: '2',
      title: '🔔  Open Notifications settings',
      description: 'A small panel will open. Find "Notifications" or "Site settings" and click it.',
    },
    {
      number: '3',
      title: '✅  Change to "Allow"',
      description: 'Change the Notifications permission from "Block" to "Allow" using the dropdown.',
    },
    {
      number: '4',
      title: '🔄  Refresh the page',
      description: 'After changing the setting, refresh this page and toggle notifications on again.',
    },
  ];

  return (
    <Modal transparent animationType="fade" visible={visible} onRequestClose={onClose}>
      <View style={ol.backdrop}>

        {/* ── Arrow callout pointing at the top-left lock icon ── */}
        <View style={ol.arrowCallout}>
          <View style={ol.triangle} />
          <View style={ol.calloutBubble}>
            <Text style={ol.calloutTitle}>👆 This icon is here</Text>
            <Text style={ol.calloutSub}>Left side of your browser's address bar</Text>
          </View>
        </View>

        {/* ── Main card ── */}
        <View style={ol.card}>

          {/* Header row */}
          <View style={ol.cardHeader}>
            <View style={ol.headerIcon}>
              <Text style={{ fontSize: 26 }}>🚫</Text>
            </View>
            <View style={{ flex: 1, marginLeft: 12 }}>
              <Text style={ol.headingText}>Notifications Blocked</Text>
              <Text style={ol.subText}>Follow these steps to re-enable them</Text>
            </View>
            <TouchableOpacity onPress={onClose} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
              <Text style={ol.closeX}>✕</Text>
            </TouchableOpacity>
          </View>

          {/* Browser bar mockup */}
          <View style={ol.browserMockup}>
            <View style={ol.browserBar}>
              {/* Lock with pulsing ring */}
              <View style={ol.lockWrap}>
                <Text style={{ fontSize: 15 }}>⊸</Text>
                <View style={ol.pulseRing} />
              </View>
              <View style={ol.urlBar}>
                <Text style={ol.urlText}>tlyfe.co.uk</Text>
              </View>
              <Text style={{ fontSize: 13, marginLeft: 8, color: '#6B7280' }}>⭐  ⋮</Text>
            </View>
            <View style={ol.browserArrowRow}>
              <View style={ol.dottedLine} />
              <Text style={ol.clickHereText}>← Click here</Text>
            </View>
          </View>

          {/* Steps list */}
          <ScrollView style={{ maxHeight: 240 }} showsVerticalScrollIndicator={false}>
            {steps.map((step, i) => (
              <View key={i} style={ol.stepRow}>
                <View style={ol.stepBadge}>
                  <Text style={ol.stepNum}>{step.number}</Text>
                </View>
                <View style={{ flex: 1, marginLeft: 12 }}>
                  <Text style={ol.stepTitle}>{step.title}</Text>
                  <Text style={ol.stepDesc}>{step.description}</Text>
                </View>
              </View>
            ))}
          </ScrollView>

          {/* Chrome tip */}
          <View style={ol.tipBox}>
            <Text style={ol.tipHeading}>💡 Chrome shortcut</Text>
            <Text style={ol.tipBody}>
              Go to{' '}
              <Text style={ol.tipBold}>
                Settings → Privacy &amp; Security → Site Settings → Notifications
              </Text>
              {' '}→ find this site under "Not allowed" and click Allow.
            </Text>
          </View>

          {/* CTA */}
          <TouchableOpacity style={ol.gotItBtn} onPress={onClose}>
            <Text style={ol.gotItText}>Got it, I'll update the settings</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

// Overlay-specific styles (plain RN Text — no ODSText prop-spreading issues)
const ol = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.78)',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    paddingTop: 32,
    paddingHorizontal: 16,
  },
  // Arrow callout
  arrowCallout: {
    alignSelf: 'flex-start',
    marginLeft: 100,
    alignItems: 'flex-start',
  },
  triangle: {
    width: 0,
    height: 0,
    borderLeftWidth: 10,
    borderRightWidth: 10,
    borderBottomWidth: 14,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderBottomColor: '#6366F1',
    marginLeft: 18,
  },
  calloutBubble: {
    backgroundColor: '#6366F1',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 10,
    borderTopLeftRadius: 2,
  },
  calloutTitle: {
    color: '#fff',
    fontWeight: '700',
    fontSize: 13,
  },
  calloutSub: {
    color: 'rgba(255,255,255,0.82)',
    fontSize: 11,
    marginTop: 2,
  },
  // Card
  card: {
    backgroundColor: '#fff',
    borderRadius: 20,
    padding: 22,
    width: '100%',
    maxWidth: 480,
    marginTop: 10,
    alignSelf: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.28,
    shadowRadius: 18,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  headerIcon: {
    width: 46,
    height: 46,
    borderRadius: 12,
    backgroundColor: '#FEE2E2',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headingText: {
    fontSize: 17,
    fontWeight: '700',
    color: '#1F2937',
  },
  subText: {
    fontSize: 13,
    color: '#6B7280',
    marginTop: 2,
  },
  closeX: {
    fontSize: 18,
    color: '#9CA3AF',
    fontWeight: '700',
    paddingLeft: 8,
  },
  // Browser mockup
  browserMockup: {
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: '#E5E7EB',
    padding: 10,
    marginBottom: 18,
  },
  browserBar: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    paddingHorizontal: 10,
    paddingVertical: 7,
  },
  lockWrap: {
    position: 'relative',
    width: 28,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 6,
  },
  pulseRing: {
    position: 'absolute',
    width: 28,
    height: 28,
    borderRadius: 14,
    borderWidth: 2,
    borderColor: '#6366F1',
    opacity: 0.8,
  },
  urlBar: {
    flex: 1,
    backgroundColor: '#F3F4F6',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  urlText: {
    fontSize: 12,
    color: '#374151',
  },
  browserArrowRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 7,
    paddingLeft: 6,
  },
  dottedLine: {
    width: 22,
    height: 0,
    borderBottomWidth: 1.5,
    borderColor: '#6366F1',
    borderStyle: 'dashed',
    marginRight: 6,
  },
  clickHereText: {
    fontSize: 12,
    color: '#6366F1',
    fontWeight: '700',
  },
  // Steps
  stepRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 14,
  },
  stepBadge: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: '#6366F1',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: 1,
  },
  stepNum: {
    color: '#fff',
    fontWeight: '700',
    fontSize: 13,
  },
  stepTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 3,
  },
  stepDesc: {
    fontSize: 13,
    color: '#6B7280',
    lineHeight: 18,
  },
  // Chrome tip
  tipBox: {
    backgroundColor: '#EFF6FF',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#BFDBFE',
    padding: 12,
    marginTop: 6,
    marginBottom: 14,
  },
  tipHeading: {
    fontSize: 13,
    fontWeight: '700',
    color: '#1D4ED8',
    marginBottom: 4,
  },
  tipBody: {
    fontSize: 12,
    color: '#1E40AF',
    lineHeight: 17,
  },
  tipBold: {
    fontWeight: '700',
    color: '#1D4ED8',
  },
  // CTA
  gotItBtn: {
    backgroundColor: '#6366F1',
    borderRadius: 12,
    paddingVertical: 13,
    alignItems: 'center',
  },
  gotItText: {
    color: '#fff',
    fontWeight: '700',
    fontSize: 15,
  },
});

// ─── Main SettingsCards Component ────────────────────────────────────────────
const SettingsCards = ({
  onClose,
  data,
  onSave,
}: {
  onClose: () => void;
  data: any;
  onSave: (form: any) => void;
}) => {
  const { colors } = useTheme();
  const [form, setForm] = useState({
    property_notification: data.property_notification,
    marketplace_notification: data.marketplace_notification,
    maintenance_notification: data.maintenance_notification,
    rental_notification: data.rental_notification,
    openflow_notification: data.openflow_notification,
  });

  const [permissionStatus, setPermissionStatus] = useState<PermissionStatus>(
    getPermissionStatus(),
  );
  const [loading, setLoading] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    const status = getPermissionStatus();
    setPermissionStatus(status);

    // Permission was already granted (e.g. after a page refresh) —
    // re-register the FCM token so the backend always has a fresh one.
    if (status === 'granted') {
      getToken(messaging, { vapidKey: VAPID_KEY })
        .then((token) => {
          console.log('FCM Token (on mount):', token);
          if (token) updateFcmToken(token);
        })
        .catch((err) => console.log('FCM token fetch error on mount:', err));
    }
  }, []);

  const handleNotificationToggle = async (value: boolean) => {
    if (!value) return;

    if (permissionStatus === 'denied') {
      setShowGuide(true);
      return;
    }

    setLoading(true);
    try {
      const result = await Notification.requestPermission();
      setPermissionStatus(result as PermissionStatus);

      if (result === 'granted') {
        const token = await getToken(messaging, { vapidKey: VAPID_KEY });
        console.log('FCM Token:', token);
        if (token) {
          updateFcmToken(token);
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem('notification_permission_asked', 'true');
            localStorage.setItem('fcm_token', token);
            localStorage.setItem('fcm_web_token', token);
          }
        }
      } else if (result === 'denied') {
        setShowGuide(true);
      }
    } catch (err) {
      console.log('Permission request error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchChange = (name: string, value: boolean) => {
    setForm({ ...form, [name]: value });
  };

  const handleSave = () => onSave(form);

  const isGranted = permissionStatus === 'granted';
  const isDenied = permissionStatus === 'denied';

  return (
    <>
      <NotificationGuideOverlay visible={showGuide} onClose={() => setShowGuide(false)} />

      <View style={{ flex: 1, ...styles.shadowBox }}>
        {/* Header */}
        <View style={{ justifyContent: 'center', alignItems: 'center', flexDirection: 'row', marginBottom: 12 }}>
          <ODSIcon icon={'TB-Notifications-Settings'} size={24} color={colors.button.main} />
          <ODSText type='h5' marginLeft={12} color={colors.text.primary}>
            Notification Settings
          </ODSText>
        </View>
        <ODSText textAlign='center' type='sm' marginTop={12} color={colors.text.primary}>
          Please select the notifications you want to receive.
        </ODSText>

        {/* ── Allow Notifications toggle ── */}
        <View
          style={{
            marginTop: 28,
            paddingBottom: 16,
            borderBottomWidth: 1,
            borderBottomColor: colors.other?.divider ?? '#E5E7EB',
          }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <View style={{ flex: 1, paddingRight: 12 }}>
              <ODSText type='md-bold' color={colors.text.primary}>
                Allow Notifications
              </ODSText>
              <ODSText
                type='sm'
                color={isDenied ? '#C2410C' : isGranted ? '#16A34A' : colors.text.secondary ?? colors.text.primary}
              >
                {isDenied
                  ? 'Blocked — tap the link below to see how to re-enable.'
                  : isGranted
                    ? 'Notifications are enabled for this device.'
                    : 'Enable to receive push notifications on this device.'}
              </ODSText>
            </View>
            <Switch
              value={isGranted}
              disabled={loading}
              onValueChange={handleNotificationToggle}
              thumbColor={isGranted ? colors.button.main : isDenied ? '#EF4444' : '#f4f3f4'}
              trackColor={{
                false: isDenied ? '#FCA5A5' : '#E5E7EB',
                true: colors.button.main + '66',
              }}
            />
          </View>

          {isDenied && (
            <TouchableOpacity onPress={() => setShowGuide(true)} style={{ marginTop: 6 }}>
              <Text style={{ fontSize: 13, color: '#6366F1', fontWeight: '600', textDecorationLine: 'underline' }}>
                ⊸  How do I enable notifications?
              </Text>
            </TouchableOpacity>
          )}
        </View>

        {/* ── Per-topic toggles ── */}
        <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 24 }}>
          <Switch value={form.openflow_notification} />
          <ODSText type='md' marginLeft={12} color={colors.text.primary}>TlyfePro</ODSText>
        </View>

        <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 24 }}>
          <Switch
            value={form.marketplace_notification}
            onValueChange={(v) => handleSwitchChange('marketplace_notification', v)}
          />
          <ODSText type='md' marginLeft={12} color={colors.text.primary}>Marketplace</ODSText>
        </View>

        <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 24 }}>
          <Switch
            value={form.rental_notification}
            onValueChange={(v) => handleSwitchChange('rental_notification', v)}
          />
          <ODSText type='md' marginLeft={12} color={colors.text.primary}>Rental Transaction</ODSText>
        </View>

        <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 24 }}>
          <Switch
            value={form.maintenance_notification}
            onValueChange={(v) => handleSwitchChange('maintenance_notification', v)}
          />
          <ODSText type='md' marginLeft={12} color={colors.text.primary}>Maintenance</ODSText>
        </View>

        <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 24, marginBottom: 40 }}>
          <Switch
            value={form.property_notification}
            onValueChange={(v) => handleSwitchChange('property_notification', v)}
          />
          <ODSText type='md' marginLeft={12} color={colors.text.primary}>Property Alert</ODSText>
        </View>

        <ODSButton onPress={handleSave}>Save</ODSButton>
        <ODSButton onPress={() => onClose()} restStyle={{ marginTop: 12 }} type='secondary'>
          Close
        </ODSButton>
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  shadowBox: {
    backgroundColor: '#fff',
    marginHorizontal: 20,
    padding: 32,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 1, height: 3 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
  },
});

export default SettingsCards;
